import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Skeleton } from "@mui/material";
import Pagination from "@mui/material/Pagination";
import moment from "moment";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import GlobalApi from "../../context/api/GlobalApi";
import Api from "./Api";

const PAGE_SIZE = 12;
const PLACEHOLDER_IMAGE =
  "https://bzpublishassets.blob.core.windows.net/media/assets/Illustration_1_mwNFaE6.png";

export default function SSoft_Blogs() {
  const activeLang = useSelector((s) => s.content.activeLanguage);

  const [boxContent, setBoxContent] = useState(null);
  const [textContent, setTextContent] = useState([]);
  const [widgetId, setWidgetId] = useState(null);
  const [listCategory, setListCategory] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState({
    category: "",
    year: "",
    industries: "",
    products: "",
    regions: "",
    subjects: "",
  });

  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [articleCount, setArticleCount] = useState(0);
  const [newsDetailPath, setNewsDetailPath] = useState("/");

  const pageCount = useMemo(
    () => Math.ceil((parseInt(articleCount || 0, 10) || 0) / PAGE_SIZE),
    [articleCount]
  );

  const getSectionsDetail = useCallback(async () => {
    try {
      const api = new Api();
      const res = await api.getSectionDetail({ language: activeLang });
      if (res?.status === 200 && Array.isArray(res.data.results)) {
        const first = res.data.results[0];
        const secWidgets = first?.widgets ?? [];

        if (secWidgets.length > 0) {
          setBoxContent(secWidgets[0]?.widget_contents?.[0] ?? null);
          setTextContent(secWidgets[2]?.widget_contents ?? []);
          setListCategory(secWidgets[1]?.article_categories ?? []);
          setWidgetId(secWidgets[1]?.id ?? null);
          setNewsDetailPath(
            secWidgets[0]?.widget_contents?.[0]?.article_language?.path ?? "/"
          );
        }
      }
    } catch (err) {
      // silently fail — keep state defaults
    }
  }, [activeLang]);

  useEffect(() => {
    getSectionsDetail();
  }, [getSectionsDetail]);

  const getAllArticles = useCallback(async () => {
    if (!widgetId) return;
    setIsLoading(true);
    try {
      const api = new GlobalApi();
      const res = await api.getArticleDetailByYearDescendingPagination(
        widgetId,
        filter.category ?? "",
        PAGE_SIZE,
        searchTerm ?? "",
        String(page),
        ""
      );

      const results = res?.data?.results ?? [];
      setFilteredArticles(results);
      setArticleCount(res?.data?.count ?? results.length);
    } catch (err) {
      setFilteredArticles([]);
      setArticleCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [widgetId, filter.category, page, searchTerm]);

  useEffect(() => {
    getAllArticles();
  }, [getAllArticles]);

  const onChangeFilter = (key) => (e) => {
    setFilter((f) => ({ ...f, [key]: e.target.value }));
  };

  const onChangeSearch = (e) => setSearchTerm(e.target.value);
  const applyFilter = () => setPage(1);

  const handleChange = (e, p) => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    setPage(p);
  };

  const renderSingleCategoryListArt = useCallback(
    (item) => {
      const asset = item?.article_assets?.[0]?.asset;
      const thumbnail =
        asset?.thumbnail_file || asset?.file_small || PLACEHOLDER_IMAGE;
      const articleTitle = item?.article_languages?.title ?? item.name ?? "-";
      const articleUrl = `${newsDetailPath}/${item?.id}/${item?.article_languages?.slug_title}`;

      return (
        <div
          key={`art-${item.id}`}
          className="group overflow-hidden rounded-lg border border-gray-200 hover:shadow-lg transition-shadow duration-300"
        >
          <div className="overflow-hidden rounded-t-lg shadow-lg">
            <img
              title={asset?.description || articleTitle}
              src={thumbnail}
              alt={asset?.description || articleTitle}
              className="drop-shadow-lg object-cover w-full h-48 transition duration-700 ease-in-out group-hover:scale-110"
              loading="lazy"
            />
          </div>

          <div className="space-y-2 mt-4 px-3 pb-3">
            <div className="text-left text-sm font-inter text-gray-600">
              {moment(item.date).isValid()
                ? moment(item.date).format("MMMM D, YYYY")
                : ""}
            </div>

            <div className="flex flex-wrap space-x-1">
              {item?.categories?.map((cat) => (
                <p
                  key={cat?.id}
                  className="text-xs font-inter rounded bg-newDarkBlue text-white px-1 w-fit"
                >
                  {cat?.name}
                </p>
              ))}
            </div>

            <Link className="cursor-pointer block" to={articleUrl || "/"}>
              <div className="font-inter-bold h-full text-lg text-left line-clamp-2 hover:text-newDarkBlue transition-colors">
                {articleTitle}
              </div>
            </Link>
          </div>
        </div>
      );
    },
    [newsDetailPath]
  );

  const renderSkeleton = () => (
    <div className="group overflow-hidden rounded-lg border border-gray-200 shadow-sm animate-pulse">
      <div className="overflow-hidden rounded-t-lg shadow-lg">
        <Skeleton variant="rectangular" width="100%" height={192} />
      </div>
      <div className="space-y-2 mt-5 px-3 pb-3">
        <Skeleton variant="text" width="40%" />
        <Skeleton variant="text" width="90%" />
        <Skeleton variant="text" width="80%" />
      </div>
    </div>
  );

  return (
    <div className="py-[90px] mt-[78px] section-container section-margin-x  relative ">
      <div className="relative z-[-1] bg-[#1a2bs35] rounded-[20px] xl:pb-[40px] mdl:pb-[160px] pb-[120px] px-[40px] pt-[40px] mdl:p-[60px] h-[400px] flex items-center">
        <div
          className="absolute inset-0 rounded-[20px] z-[-1] bg-cover bg-center w-full"
          style={{
            backgroundImage: `url(${boxContent?.assets?.[0]?.asset_file})`,
          }}
        />

        <h1 className="text-white header-style-hero">
          {boxContent?.article_language?.title}
        </h1>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          applyFilter();
        }}
        className="flex max-w-4xl mx-auto -mt-[20px] z-[20]"
        role="search"
        aria-label={textContent?.[0]?.content}
      >
        <input
          type="search"
          placeholder={textContent?.[0]?.content}
          value={searchTerm}
          onChange={onChangeSearch}
          className="flex-grow border rounded-l-lg px-4 py-3 text-gray-700 placeholder-gray-400 focus:outline-none"
          aria-label="Search blog articles"
        />
        <button
          type="submit"
          className="all-button bg-newDarkBlue !rounded-l-none"
          aria-label={textContent?.[1]?.content}
        >
          {textContent?.[1]?.content}
        </button>
      </form>

      <div className="bg-[#FAF8F5] rounded-lg p-6 mx-auto my-[60px]">
        <h2 className="font-semibold text-lg mb-4">
          {textContent?.[2]?.content}
        </h2>
        <div className="flex flex-wrap gap-4">
          <select
            value={filter.industries}
            onChange={onChangeFilter("industries")}
            className="min-w-[180px] bg-white rounded-md border border-gray-300 px-4 py-2"
            aria-label="Filter by Industries"
          >
            {listCategory?.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-2 mb-4">
        <h3 className="text-xl font-semibold mb-1">
          {textContent?.[3]?.content}
        </h3>
        <p className="text-gray-600">
          {articleCount === 0
            ? textContent?.[5]?.content
            : `${textContent?.[4]?.content} ${
                page * PAGE_SIZE - PAGE_SIZE + 1
              } - ${Math.min(
                page * PAGE_SIZE,
                articleCount
              )} of ${articleCount}`}
        </p>
      </div>

      <div className="max-w-7xl mx-auto px-2">
        <div className="grid lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-2 grid-cols-1 gap-6">
          {isLoading
            ? Array.from({ length: PAGE_SIZE }).map((_, index) => (
                <div key={`skeleton-${index}`}>{renderSkeleton()}</div>
              ))
            : filteredArticles.map((list) => renderSingleCategoryListArt(list))}
        </div>

        {!isLoading && filteredArticles.length === 0 && (
          <div className="text-center py-12 text-gray-500 text-lg">
            {textContent?.[6]?.content}
          </div>
        )}
      </div>

      {pageCount > 1 && (
        <div className="mt-12 flex items-center justify-end gap-x-5 max-w-7xl mx-auto px-2">
          <Pagination
            count={pageCount}
            color="primary"
            size="medium"
            page={page}
            variant="outlined"
            shape="rounded"
            onChange={handleChange}
            aria-label="Pagination Navigation"
          />
        </div>
      )}
    </div>
  );
}
